
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

interface RiskAssessment {
  id?: string;
  risk_name: string;
  risk_description: string;
  risk_category: string;
  likelihood: number;
  impact: number;
  mitigation_strategy: string;
  responsible_person: string;
  review_date: string;
  status: string;
}

interface IndustryRisk {
  id: string;
  risk_name: string;
  risk_description: string;
  mitigation_strategy: string;
  risk_category: string;
  industry_category: string;
  industry_subcategory: string;
  industry_tags: string[];
  likelihood: number;
  impact: number;
}

interface EnhancedRiskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  risk?: RiskAssessment;
  industryRisk?: IndustryRisk;
}

export const EnhancedRiskModal = ({ isOpen, onClose, onSave, risk, industryRisk }: EnhancedRiskModalProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<RiskAssessment>({
    risk_name: '',
    risk_description: '',
    risk_category: 'operational',
    likelihood: 1,
    impact: 1,
    mitigation_strategy: '',
    responsible_person: '',
    review_date: '',
    status: 'active'
  });

  const categories = ['climate', 'regulatory', 'operational', 'financial', 'reputational'];
  const statuses = ['active', 'mitigated', 'closed'];

  useEffect(() => {
    if (risk) {
      setFormData(risk);
    } else if (industryRisk) {
      // Pre-populate with industry risk data
      setFormData({
        risk_name: industryRisk.risk_name,
        risk_description: industryRisk.risk_description,
        risk_category: industryRisk.risk_category,
        likelihood: industryRisk.likelihood || 1,
        impact: industryRisk.impact || 1,
        mitigation_strategy: industryRisk.mitigation_strategy,
        responsible_person: '',
        review_date: '',
        status: 'active'
      });
    } else {
      // Reset form for new risk
      setFormData({
        risk_name: '',
        risk_description: '',
        risk_category: 'operational',
        likelihood: 1,
        impact: 1,
        mitigation_strategy: '',
        responsible_person: '',
        review_date: '',
        status: 'active'
      });
    }
  }, [risk, industryRisk, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Modal submit - risk:', risk, 'industryRisk:', industryRisk, 'formData:', formData);
      
      // If we have an industry risk, pass the original industry risk data
      // Otherwise, pass the form data for regular risk assessments
      if (industryRisk) {
        await onSave(industryRisk);
      } else {
        await onSave(formData);
      }
      
      // Close modal and reset form
      onClose();
    } catch (error: any) {
      console.error('Error in modal submit:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save risk assessment",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof RiskAssessment, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getModalTitle = () => {
    if (risk) return 'Edit Risk Assessment';
    if (industryRisk) return 'Add Risk from Industry Database';
    return 'Add New Risk Assessment';
  };

  const getSubmitButtonText = () => {
    if (risk) return 'Update Risk Assessment';
    if (industryRisk) return 'Add to My Assessments';
    return 'Add Risk Assessment';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getModalTitle()}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="risk_name">Risk Name *</Label>
              <Input
                id="risk_name"
                value={formData.risk_name}
                onChange={(e) => handleInputChange('risk_name', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="risk_category">Risk Category *</Label>
              <Select value={formData.risk_category} onValueChange={(value) => handleInputChange('risk_category', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="risk_description">Risk Description *</Label>
            <Textarea
              id="risk_description"
              value={formData.risk_description}
              onChange={(e) => handleInputChange('risk_description', e.target.value)}
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="likelihood">Likelihood (1-5) *</Label>
              <Select value={formData.likelihood.toString()} onValueChange={(value) => handleInputChange('likelihood', parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="impact">Impact (1-5) *</Label>
              <Select value={formData.impact.toString()} onValueChange={(value) => handleInputChange('impact', parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mitigation_strategy">Mitigation Strategy</Label>
            <Textarea
              id="mitigation_strategy"
              value={formData.mitigation_strategy}
              onChange={(e) => handleInputChange('mitigation_strategy', e.target.value)}
              rows={3}
              placeholder="Describe strategies to mitigate this risk"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="responsible_person">Responsible Person</Label>
              <Input
                id="responsible_person"
                value={formData.responsible_person}
                onChange={(e) => handleInputChange('responsible_person', e.target.value)}
                placeholder="Enter responsible person"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="review_date">Review Date</Label>
              <Input
                id="review_date"
                type="date"
                value={formData.review_date}
                onChange={(e) => handleInputChange('review_date', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map(status => (
                  <SelectItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {industryRisk && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Industry Risk Information</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Industry Category:</strong> {industryRisk.industry_category}</p>
                <p><strong>Industry Subcategory:</strong> {industryRisk.industry_subcategory}</p>
                <p><strong>Tags:</strong> {industryRisk.industry_tags?.join(', ')}</p>
                <p className="text-xs mt-2">
                  This risk will be added to your personal assessments and can be customized further.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                getSubmitButtonText()
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
